/**
 * Como se nombra cada estado de un pedido en el backoffice (ADM-09). Lo usan
 * la tabla del historial, sus filtros y el modal del detalle: el mismo estado
 * no puede llamarse de dos maneras en la misma pantalla.
 */
import type {
  CorreoPedidoAdmin,
  EstadoPedido,
  Pedido,
  PedidoAdminDetalle,
  TipoCorreoPedido,
} from '@bandu/compartido';

import { numero } from './formato';

export interface Rotulo {
  clase: string;
  texto: string;
}

export const ESTADO_PAGO: Record<EstadoPedido, Rotulo> = {
  pagado: { clase: 'etq etq-ok', texto: 'Pagado' },
  pendiente_pago: { clase: 'etq etq-neutra', texto: 'Sin pagar' },
  rechazado: { clase: 'etq etq-critico', texto: 'Pago rechazado' },
  anulado: { clase: 'etq etq-neutra', texto: 'Anulado' },
};

type ConDocumento = Pick<Pedido, 'estado' | 'tipoDocumento' | 'folioDocumento'>;

export function nombreDocumento(pedido: Pick<Pedido, 'tipoDocumento'>): string {
  return pedido.tipoDocumento === 'factura' ? 'Factura' : 'Boleta';
}

/**
 * La boleta o factura (DOC-01). Un pedido pagado sin folio es trabajo
 * pendiente con alguien que ya pago, y va en ambar. Uno que no se pago no lleva
 * documento: no hay nada que reclamar.
 */
export function rotuloDocumento(pedido: ConDocumento): Rotulo {
  const nombre = nombreDocumento(pedido);
  if (pedido.folioDocumento !== null) {
    return { clase: 'etq etq-ok', texto: `${nombre} ${pedido.folioDocumento}` };
  }
  if (pedido.estado === 'pagado') {
    return { clase: 'etq etq-alerta', texto: `Falta ${nombre.toLowerCase()}` };
  }
  return { clase: 'etq etq-neutra', texto: `${nombre}, no corresponde` };
}

/** Dice "Correo de…": "Confirmacion de compra" a secas se leia como un tramite del cliente. */
export const TIPO_CORREO: Record<TipoCorreoPedido, string> = {
  confirmacion_pedido: 'Correo de confirmacion de compra',
  documento_emitido: 'Correo con la boleta o factura',
  acceso_administrador: 'Acceso al panel',
  aviso_venta_nueva: 'Aviso de venta al negocio',
};

/**
 * Estado de un correo en la bitacora. `pendiente` es que todavia no sale (el
 * envio en curso o reintentandose): se dice "Por enviar" y no "Pendiente", que
 * al lado de "Confirmacion de compra" parecia esperar algo del cliente.
 */
export function estadoCorreo(correo: Pick<CorreoPedidoAdmin, 'estado' | 'intentos'>): Rotulo {
  if (correo.estado === 'enviado') return { clase: 'etq etq-ok', texto: 'Enviado' };
  if (correo.estado === 'fallido') {
    return { clase: 'etq etq-critico', texto: `No se pudo enviar (${numero(correo.intentos)} intentos)` };
  }
  return { clase: 'etq etq-neutra', texto: 'Por enviar' };
}

/** "(3 unidades)", junto al titulo de lo comprado. */
export function unidadesPedido(pedido: Pick<PedidoAdminDetalle, 'items'>): string {
  const unidades = pedido.items.reduce((suma, item) => suma + item.cantidad, 0);
  return `(${numero(unidades)} ${unidades === 1 ? 'unidad' : 'unidades'})`;
}
